/**
 * Formats an address string by capitalizing words and ensuring proper punctuation.
 *
 * @param {string} address - The raw address string to format.
 * @returns {string} - The formatted address string.
 */

import { STRINGS } from "../constants/strings";

export const parkingFormatAddress = (address) => {
  if (!address) return "Address not available";
  const exclusions = ["and", "of", "the", "at", "by"];

  return address
    .replace(/\n/g, ", ") // Replace newline with comma and space
    .replace(/\s*,\s*/g, ", ") // Ensure proper spacing around commas
    .split(/\s+/) // Split by any whitespace
    .map((word) => {
      if (word.length === 2 && word === word.toUpperCase()) {
        return word; // Preserve two-letter uppercase words (e.g., "VA")
      }
      return exclusions.includes(word.toLowerCase())
        ? word.toLowerCase() // Keep exclusions in lowercase
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(); // Capitalize first letter
    })
    .join(" ");
};

/**
 * Converts a string to title case.
 *
 * @param {string} str - The string to convert to title case.
 * @returns {string} - The converted title case string.
 */
export const toTitleCase = (str) => {
  try {
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  } catch (error) {
    console.log(STRINGS.dataUnavailable, error);
    return STRINGS.dataUnavailable;
  }
};

/**
 * Formats and combines address, city, and ZIP code into a single string.
 *
 * The function constructs a single string address by formatting and concatenating the street address,
 * city, and ZIP code. The state abbreviation "VA" is included as part of the formatted string.
 * The function ensures the "VA - ZIP" appears as a non-breaking unit to avoid layout issues on small screens.
 *
 * @param {string} address - The street address of the event location.
 * @param {string} city - The city where the event is located.
 * @param {string} zip - The ZIP code for the event location.
 * @returns {string} The formatted address string in the format "Street Address, City, VA - ZIP".
 *                   Returns "Address not available" if any input is missing.
 */
export const formatAddress = (address, city, zip) => {
  if (!address || !city || !zip) return "Address not available";

  const cityStateZip = `${city}, VA ${zip}`;

  return `${address}, ${cityStateZip}`;
};

/**
 * Ensures the provided date is valid.
 *
 * @param {Date|string|null} selectedDate - The date to validate.
 * @returns {Date} - A valid JavaScript Date object.
 */
export const getValidDate = (selectedDate) => {
  const dateObj = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
  return !isNaN(dateObj.getTime()) ? dateObj : new Date(); // Return valid date or current date
};

/**
 * Sanitize and validate latitude/longitude
 */
export const sanitizeCoordinate = (value) => {
  if (typeof value === "string") {
    return parseFloat(value.replace(/'/g, ""));
  } else if (typeof value === "number") {
    return value; // Already a valid number
  }
  return null; // Return null for invalid values
};

/**
 * Converts a distance from meters to miles.
 *
 * @param {number} meters - The distance in meters.
 * @param {number} decimal - The number of decimal places to include in the result.
 * @returns {string} The distance in miles, formatted to the specified number of decimal places.
 */
export const convertMetersToMiles = (meters, decimal) => {
  return (meters * 0.000621371).toFixed(decimal);
};

/**
 * Formats the given hours string by trimming leading whitespace,
 * inserting a newline before the first digit, and appending a space at the end.
 *
 * @param {string} hours - The hours string to format.
 * @returns {string} The formatted hours string.
 */
export const formatBathroomHours = (hours) => {
  return hours.trimStart().replace(/(\d)/, "\n$1") + " ";
};

/**
 * Creates a new string label that defining the type of parking.
 * @param {*} param0
 * @returns The formatted parking type string based on parameters provided
 */
export const getParkingTypeLabel = ({ type, isPublic }) => {
  const parkingAccessLabel = isPublic ? "Public" : "Private";
  return `${toTitleCase(type)} Parking - ${parkingAccessLabel}`;
};
